/**
 * The context space for one (type, hazard) as a field: building use down,
 * person presence across.
 *
 * This is the review surface. Judging a rule set one combination at a time
 * hides its shape; as a field it is visible at a glance that the garage row
 * stays flat, or that a building-scope hazard leaves the whole person-presence
 * axis inert.
 *
 * Encoding is deliberately in three discrete states — lifted, unchanged,
 * lowered — never a gradient. The scale has four rungs and context moves at
 * most one; a continuous ramp would imply values between them that the model
 * does not have. The arrow carries the same information as the colour so the
 * grid stays readable without relying on colour alone.
 *
 * Only the selected cell shows its number. With κ ∈ {−1, 0, +1} every other
 * cell is one of three outcomes relative to the base, so 64 printed values
 * carried almost no information and buried the shape the field exists to show.
 * The value for any cell is still one hover away, and the band bar below spells
 * out the selected one.
 */

import "../../App.sass";
import React from "react";
import { useTranslation } from "react-i18next";

interface Cell {
  personExposure: string;
  siValue: number | null;
  kappa: number;
  firedRules: string[];
}

interface Row {
  usageClass: string;
  name: string;
  cells: Cell[];
}

interface Properties {
  matrix: {
    rows: Row[];
    personExposures: string[];
    baseValue: number | null;
    flatRows: string[];
    flatExposureAxis?: boolean;
    hazard: { label: string; damageType: string; effectScope: string };
  };
  selectedUsageClass?: string | null;
  selectedPersonExposure?: string | null;
  onSelect: (usageClass: string, personExposure: string) => void;
}

const cellColors = (kappa: number): { background: string; color: string; border: string } => {
  if (kappa > 0) return { background: "#fde7e9", color: "#a4262c", border: "#f5c6cb" };
  if (kappa < 0) return { background: "#dff6dd", color: "#0b6a0b", border: "#c3e6cb" };
  return { background: "transparent", color: "#444", border: "transparent" };
};

export default function ContextMatrix(properties: Properties) {
  const { t } = useTranslation();
  const { matrix, selectedUsageClass, selectedPersonExposure, onSelect } = properties;
  if (!matrix?.rows?.length) return null;

  const flat = new Set(matrix.flatRows || []);

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "separate", borderSpacing: "2px", fontSize: "12px" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "2px 8px", fontWeight: 400, color: "#666" }}>
              {t("context.usageClass")}
            </th>
            {matrix.personExposures.map((exposure) => (
              <th
                key={exposure}
                style={{
                  padding: "2px 8px",
                  fontWeight: 400,
                  color: "#666",
                  minWidth: "78px",
                  textAlign: "center",
                }}
              >
                {t(`context.personExposureValue.${exposure}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.rows.map((row) => {
            const rowIsFlat = flat.has(row.usageClass);
            return (
              <tr key={row.usageClass}>
                <td
                  style={{
                    padding: "2px 8px",
                    whiteSpace: "nowrap",
                    color: rowIsFlat ? "#999" : "#333",
                  }}
                  title={rowIsFlat ? t("context.matrix.flatRow") : undefined}
                >
                  {row.name}
                </td>
                {row.cells.map((cell) => {
                  const isSelected =
                    row.usageClass === selectedUsageClass &&
                    cell.personExposure === selectedPersonExposure;
                  const colors = cellColors(cell.kappa);
                  const arrow = cell.kappa > 0 ? "↑" : cell.kappa < 0 ? "↓" : "";
                  const title = [
                    `${row.name} · ${t(`context.personExposureValue.${cell.personExposure}`)}`,
                    cell.siValue === null
                      ? t("context.matrix.noBase")
                      : `${t("context.simulator.resultValue")} ${cell.siValue}`,
                    cell.firedRules.length > 0
                      ? `${t("context.matrix.firedRules")}: ${cell.firedRules.join(", ")}`
                      : t("context.matrix.noRuleFired"),
                  ].join(" · ");

                  return (
                    <td key={cell.personExposure} style={{ padding: 0 }}>
                      <button
                        type="button"
                        title={title}
                        onClick={() => onSelect(row.usageClass, cell.personExposure)}
                        style={{
                          width: "100%",
                          minWidth: "58px",
                          padding: "5px 6px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontVariantNumeric: "tabular-nums",
                          backgroundColor: colors.background,
                          color: cell.siValue === null ? "#999" : colors.color,
                          border: isSelected
                            ? "2px solid #004578"
                            : `1px solid ${colors.border === "transparent" ? "#eee" : colors.border}`,
                          borderRadius: "3px",
                          fontWeight: isSelected ? 700 : 400,
                          // Only the selection moves; the field itself must not
                          // shimmer while someone is reading it.
                          transition: "border-color 160ms ease, background-color 160ms ease",
                        }}
                      >
                        {cell.siValue === null ? (
                          "—"
                        ) : isSelected ? (
                          <>
                            {cell.siValue}
                            {arrow ? <span style={{ marginLeft: "3px" }}>{arrow}</span> : null}
                          </>
                        ) : (
                          // Shape, not spreadsheet: the arrow says what happens,
                          // the dot marks "nothing happens" so the cell still
                          // reads as answered rather than empty.
                          <span style={{ opacity: cell.kappa === 0 ? 0.35 : 1 }}>
                            {arrow || "·"}
                          </span>
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div
        className="marginTopS displayFlex alignItemsCenter gapM"
        style={{ fontSize: "11px", color: "#666", flexWrap: "wrap" }}
      >
        <span>
          {t("context.matrix.base")}: <strong>{matrix.baseValue ?? "—"}</strong>
        </span>
        <span style={{ color: "#a4262c" }}>↑ {t("context.matrix.lifted")}</span>
        <span style={{ color: "#0b6a0b" }}>↓ {t("context.matrix.lowered")}</span>
        <span>{t("context.matrix.unchanged")}</span>
        {/* A whole axis that never moves is the answer to "does collecting this
            attribute pay for itself?" — worth stating, not just implying. */}
        {matrix.flatExposureAxis ? (
          <span style={{ color: "#8a6d00" }}>
            {t("context.matrix.exposureInert", {
              scope: t(`context.effectScopeValue.${matrix.hazard.effectScope}`),
            })}
          </span>
        ) : null}
      </div>
    </div>
  );
}
